'use client';

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '../../lib/utils';

export interface GLPSecondaryNavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  external?: boolean;
  active?: boolean;
  onClick?: () => void;
}

interface GLPSecondarySidebarContextValue {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
}

const GLPSecondarySidebarContext = React.createContext<GLPSecondarySidebarContextValue | null>(
  null,
);

export function useGLPSecondarySidebar() {
  const ctx = React.useContext(GLPSecondarySidebarContext);
  if (!ctx) {
    throw new Error('useGLPSecondarySidebar must be used within a GLPSecondarySidebarProvider.');
  }
  return ctx;
}

interface GLPSecondarySidebarProviderProps {
  children: React.ReactNode;
  defaultCollapsed?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export function GLPSecondarySidebarProvider({
  children,
  defaultCollapsed = false,
  collapsed: collapsedProp,
  onCollapsedChange,
}: GLPSecondarySidebarProviderProps) {
  const [_collapsed, _setCollapsed] = React.useState(defaultCollapsed);
  const collapsed = collapsedProp ?? _collapsed;

  const setCollapsed = React.useCallback(
    (value: boolean) => {
      if (onCollapsedChange) onCollapsedChange(value);
      else _setCollapsed(value);
    },
    [onCollapsedChange],
  );

  const toggleCollapsed = React.useCallback(
    () => setCollapsed(!collapsed),
    [collapsed, setCollapsed],
  );

  const value = React.useMemo(
    () => ({ collapsed, setCollapsed, toggleCollapsed }),
    [collapsed, setCollapsed, toggleCollapsed],
  );

  return (
    <GLPSecondarySidebarContext.Provider value={value}>
      {children}
    </GLPSecondarySidebarContext.Provider>
  );
}

interface GLPSecondarySidebarProps extends React.ComponentProps<'aside'> {
  navItems: GLPSecondaryNavItem[];
  title?: string;
  currentPath?: string;
  onNavigate?: (path: string) => void;
  footer?: React.ReactNode;
  showFooter?: boolean;
  expandedWidth?: string;
  collapsedWidth?: string;
  /**
   * Opt-in mobile layout: hidden below `lg` (1024px), where the section links
   * are reached through the responsive `GLPSidebar` drawer's drill-down
   * (pass them as the module's `children`). From `lg` up it renders exactly
   * as without it. Off by default.
   */
  responsive?: boolean;
}

export function GLPSecondarySidebar({
  className,
  navItems,
  title,
  currentPath,
  onNavigate,
  footer,
  showFooter = false,
  expandedWidth = '14rem',
  collapsedWidth = '3.5rem',
  responsive = false,
  ...props
}: GLPSecondarySidebarProps) {
  const { collapsed, toggleCollapsed } = useGLPSecondarySidebar();

  const isItemActive = (item: GLPSecondaryNavItem) => {
    if (typeof item.active === 'boolean') return item.active;
    if (currentPath && item.path) return currentPath === item.path;
    return false;
  };

  return (
    <aside
      className={cn(
        'shrink-0 h-full flex flex-col overflow-hidden bg-[hsl(var(--color-primary)/0.08)] transition-[width] duration-300 ease-in-out',
        responsive && 'hidden lg:flex',
        className,
      )}
      style={{ width: collapsed ? collapsedWidth : expandedWidth, paddingTop: '3rem' }}
      {...props}
    >
      {/* Toggle row with optional title */}
      <button
        onClick={toggleCollapsed}
        className='flex items-center gap-2 px-3 py-2 text-neutral-700 hover:opacity-80 transition-opacity'
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight className='h-5 w-5 flex-shrink-0' />
        ) : (
          <ChevronLeft className='h-5 w-5 flex-shrink-0' />
        )}
        {title && !collapsed && (
          <span className='text-sm font-medium text-[hsl(var(--color-primary))] truncate'>
            {title}
          </span>
        )}
      </button>

      {/* Navigation */}
      <nav className='flex-1 min-h-0 overflow-y-auto pl-2 pt-2 space-y-2'>
        {navItems.map((item) => (
          <GLPSecondarySidebarNavItem
            key={item.label}
            item={item}
            collapsed={collapsed}
            isActive={isItemActive(item)}
            onNavigate={onNavigate}
          />
        ))}
      </nav>

      {showFooter && footer && !collapsed && (
        <div className='flex-shrink-0 border-t border-[hsl(var(--color-primary)/0.1)]'>
          {footer}
        </div>
      )}
    </aside>
  );
}

interface GLPSecondarySidebarNavItemProps {
  item: GLPSecondaryNavItem;
  collapsed: boolean;
  isActive: boolean;
  onNavigate?: (path: string) => void;
}

function GLPSecondarySidebarNavItem({
  item,
  collapsed,
  isActive,
  onNavigate,
}: GLPSecondarySidebarNavItemProps) {
  const Icon = item.icon;

  const handleClick = (e: React.MouseEvent) => {
    if (item.onClick) {
      e.preventDefault();
      item.onClick();
      return;
    }
    if (onNavigate && !item.external) {
      e.preventDefault();
      onNavigate(item.path);
    }
  };

  const className = cn(
    'group flex items-center gap-3 p-2 rounded-l-[19px] transition-all duration-300',
    isActive
      ? 'bg-white text-[hsl(var(--color-primary))] font-semibold'
      : 'text-neutral-600 hover:bg-white/90 hover:text-[hsl(var(--color-primary))] hover:font-semibold',
  );

  const iconClassName = cn(
    'h-5 w-5 flex-shrink-0 transition-colors duration-300',
    isActive
      ? 'text-[hsl(var(--color-primary))]'
      : 'text-neutral-600 group-hover:text-[hsl(var(--color-primary))]',
  );

  const labelClassName = cn(
    'text-sm truncate transition-colors duration-300',
    isActive
      ? 'text-[hsl(var(--color-primary))]'
      : 'text-neutral-600 group-hover:text-[hsl(var(--color-primary))]',
  );

  const content = (
    <>
      <Icon className={iconClassName} />
      {!collapsed && <span className={labelClassName}>{item.label}</span>}
    </>
  );

  if (item.external) {
    return (
      <a
        href={item.path}
        target='_blank'
        rel='noopener noreferrer'
        title={item.label}
        className={className}
        onClick={handleClick}
      >
        {content}
      </a>
    );
  }

  return (
    <a href={item.path} title={item.label} className={className} onClick={handleClick}>
      {content}
    </a>
  );
}
